import React, { useMemo, useState } from 'react';
import { useSupervision, agentLights } from '../../hooks/useSupervision';
import { fmtTime, fmtDate, timeAgo } from '../chat/format';
import { ORIGIN } from '../chat/status';
import {
  INTEGRATION_STATUS, PRESENCE, SESSION_STATE, describeEvent, phoneOf, severityIcon,
} from '../supervision/labels';
import Talkback from '../supervision/Talkback';

/**
 * Supervisión en vista simple (Fase 9): integraciones, agentes de IA, asesores,
 * alertas y hoja de sesión. Funciona sin el Estudio: es la misma información en tablas.
 */
export default function SupervisionPanel({ onOpenStudio }) {
  const { data, error, live, now } = useSupervision();
  const [stateFilter, setStateFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');

  const agentName = useMemo(() => {
    const names = new Map((data?.ai_agents || []).map(a => [a.id, a.name]));
    return id => names.get(id) || id;
  }, [data]);

  const rows = useMemo(() => (data?.sessions.rows || []).filter(r =>
    (stateFilter === 'all' || r.state === stateFilter) &&
    (userFilter === 'all' || String(r.assigned_user_id) === userFilter)), [data, stateFilter, userFilter]);

  if (!data) return error ? <div style={s.errorBox}>⚠️ {error}</div> : <div style={s.muted}>Cargando…</div>;

  const totalSessions = Object.values(data.sessions.counts).reduce((a, b) => a + b, 0);

  return (
    <div style={s.wrap}>
      {error && <div style={s.errorBox}>⚠️ {error}</div>}
      {onOpenStudio && (
        <div><button style={s.chip} onClick={onOpenStudio}>🎛 Abrir {data.studio?.title || 'el estudio'}</button></div>
      )}

      <section style={s.talkCard}>
        <Talkback name={data.ai_agents.find(a => a.room === 'control')?.name} compact />
      </section>

      {/* Integraciones */}
      <section>
        <h3 style={s.h3}>Integraciones</h3>
        <div style={s.integrations}>
          {data.integrations.map(i => {
            const st = INTEGRATION_STATUS[i.status] || INTEGRATION_STATUS.unknown;
            return (
              <div key={i.name} style={{ ...s.integration, ...(i.status === 'down' ? s.integrationDown : {}) }}
                title={i.checked_at ? `Revisada ${fmtTime(i.checked_at)}` : ''}>
                <div style={s.integrationName}>{i.label}</div>
                <div style={s.integrationStatus}>
                  <span style={{ color: st.color }}>{st.icon}</span> {st.label}
                  {i.status === 'up' && i.latency_ms != null && <span style={s.muted}> · {i.latency_ms} ms</span>}
                </div>
                {i.status === 'down' && (
                  <div style={s.integrationError}>{i.last_error} · desde {fmtTime(i.changed_at)}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div style={s.columns}>
        {/* Agentes de IA */}
        <section style={s.col}>
          <h3 style={s.h3}>Agentes de IA</h3>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Agente</th>
                  <th style={s.th}>Estado</th>
                  <th style={s.thNum}>Turnos 15 min</th>
                  <th style={s.thNum}>Errores 1 h</th>
                  <th style={s.thNum}>Último turno</th>
                </tr>
              </thead>
              <tbody>
                {data.ai_agents.map(a => (
                  <tr key={a.id}>
                    <td style={s.td}>
                      <div>{a.name}</div>
                      <div style={s.muted}>{a.role}</div>
                    </td>
                    <td style={s.td}>
                      {agentLights(live, a.id, now).busy ? <span style={s.busy}>● Procesando</span> : <span style={s.muted}>En espera</span>}
                    </td>
                    <td style={s.tdNum}>{a.turns_15m}</td>
                    <td style={s.tdNum}>{a.errors_1h ? <span title={`Último ${fmtTime(a.last_error_at)}`}>⚠ {a.errors_1h}</span> : 0}</td>
                    <td style={s.tdNum}>{a.last_turn_at ? timeAgo(a.last_turn_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Asesores */}
        <section style={s.col}>
          <h3 style={s.h3}>Asesores <span style={s.count}>cola: {data.queue.count}</span></h3>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Asesor</th>
                  <th style={s.th}>Estado</th>
                  <th style={s.thNum}>Casos abiertos</th>
                </tr>
              </thead>
              <tbody>
                {data.humans.length === 0 && <tr><td style={s.td} colSpan={3}>Sin usuarios activos.</td></tr>}
                {data.humans.map(h => {
                  const p = PRESENCE[h.presence] || PRESENCE.offline;
                  return (
                    <tr key={h.id}>
                      <td style={s.td}>
                        {h.name}
                        {h.role === 'admin' && <span style={s.muted}> · admin</span>}
                        <span style={s.muted}> · {h.agent_type_label}</span>
                        {!h.receives_assignments && <span style={s.muted}> · no recibe casos</span>}
                      </td>
                      <td style={s.td}>{p.icon} {p.label}</td>
                      <td style={s.tdNum}>{h.open_cases}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Alertas */}
      <section>
        <h3 style={s.h3}>Alertas de las últimas 24 horas <span style={s.count}>{data.alerts.length}</span></h3>
        {data.alerts.length === 0 ? (
          <div style={s.empty}>Sin alertas. Todo en orden.</div>
        ) : (
          <div style={s.alerts}>
            {data.alerts.map(a => (
              <div key={a.id} style={{ ...s.alert, ...(a.severity === 'error' ? s.alertError : {}) }}>
                <span>{severityIcon(a.severity)}</span>
                <span style={{ flex: 1 }}>{describeEvent(a, agentName)}</span>
                <span style={s.muted}>{fmtDate(a.created_at)} {fmtTime(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Hoja de sesión */}
      <section>
        <h3 style={s.h3}>Hoja de sesión</h3>
        <div style={s.filters}>
          <button style={{ ...s.chip, ...(stateFilter === 'all' ? s.chipActive : {}) }} onClick={() => setStateFilter('all')}>
            Todos · {totalSessions}
          </button>
          {Object.entries(SESSION_STATE).map(([key, st]) => (
            <button key={key} style={{ ...s.chip, ...(stateFilter === key ? s.chipActive : {}) }} onClick={() => setStateFilter(key)}>
              {st.icon} {st.label} · {data.sessions.counts[key] || 0}
            </button>
          ))}
          <select style={s.select} value={userFilter} onChange={e => setUserFilter(e.target.value)}>
            <option value="all">Todos los asesores</option>
            {data.humans.map(h => <option key={h.id} value={String(h.id)}>{h.name}</option>)}
          </select>
        </div>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Conversación</th>
                <th style={s.th}>Estado</th>
                <th style={s.th}>Asesor</th>
                <th style={s.th}>Origen / motivo</th>
                <th style={s.thNum}>En este estado</th>
                <th style={s.thNum}>Último mensaje</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td style={s.td} colSpan={6}>No hay casos con este filtro.</td></tr>}
              {rows.map(r => {
                const st = SESSION_STATE[r.state];
                const since = r.state === 'queued' ? r.queued_at : r.state === 'closed' ? r.closed_at : r.assigned_at;
                return (
                  <tr key={r.session_id}>
                    <td style={s.td}>{phoneOf(r.session_id)}</td>
                    <td style={s.td}>{st.icon} {st.label}{r.state === 'closed' && r.closed_reason === 'inactivity' && <span style={s.muted}> · por inactividad</span>}</td>
                    <td style={s.td}>{r.assigned_name || <span style={s.muted}>—</span>}</td>
                    <td style={s.td}>
                      {r.origin ? ORIGIN[r.origin] || r.origin : <span style={s.muted}>—</span>}
                      {r.escalation_reason && <span style={s.muted}> · {r.escalation_reason}</span>}
                    </td>
                    <td style={s.tdNum}>{since ? timeAgo(since) : '—'}</td>
                    <td style={s.tdNum}>{r.last_message_at ? `${fmtDate(r.last_message_at)} ${fmtTime(r.last_message_at)}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div style={s.muted}>Actualizado {fmtTime(data.generated_at)} · se actualiza solo con cada evento.</div>
    </div>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 22 },
  muted: { color: 'var(--text-3)', fontSize: 12 },
  h3: { fontSize: 13, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 10px', display: 'flex', gap: 8, alignItems: 'center' },
  count: { background: 'var(--surface-2)', borderRadius: 10, padding: '1px 8px', fontSize: 11, color: 'var(--text-2)', textTransform: 'none', letterSpacing: 0 },
  integrations: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 },
  integration: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  integrationDown: { border: '1px solid var(--danger-border)', background: 'var(--danger-soft)' },
  integrationName: { fontSize: 13, fontWeight: 700, color: 'var(--text)' },
  integrationStatus: { fontSize: 12, color: 'var(--text-2)' },
  integrationError: { fontSize: 11, color: 'var(--danger)' },
  columns: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  col: { flex: '1 1 380px', minWidth: 0 },
  busy: { color: 'var(--accent-text)', fontWeight: 600 },
  empty: { color: 'var(--text-3)', fontSize: 13, padding: '10px 0' },
  alerts: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' },
  alert: { display: 'flex', gap: 10, alignItems: 'baseline', background: 'var(--warning-soft)', border: '1px solid var(--warning-border)', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: 'var(--text)' },
  alertError: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)' },
  filters: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 },
  chip: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 20, padding: '5px 12px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' },
  chipActive: { background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', color: 'var(--text)' },
  select: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '5px 8px', color: 'var(--text)', fontSize: 12 },
  talkCard: { background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: 14 },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13 },
  tableWrap: { overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  thNum: { textAlign: 'right', padding: '10px 12px', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text)', verticalAlign: 'top' },
  tdNum: { padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', verticalAlign: 'top', whiteSpace: 'nowrap' },
};
